import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Button, Card, Field } from '@gatecheckpro/design-system';
import { slugSchema, timezoneSchema, type OrganizerEvent } from '@gatecheckpro/contracts';
import { useCommand } from '../lib/useCommand.ts';
import { useResource } from '../lib/useResource.ts';
import { FailureNotice } from '../lib/refusal.tsx';
import { useViewer } from '../lib/session.tsx';

interface VenueSummary {
  readonly venueId: string;
  readonly name: string;
  readonly publicLocality: string;
  readonly timezone: string;
}

function suggestSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 96);
}

/**
 * Creating an event.
 *
 * Four fields, because everything else can be added in the workspace. Asking for
 * gates and capacity before the organizer has even named the show would be a
 * form nobody finishes.
 */
export function CreateEventPage() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const navigate = useNavigate();
  const viewer = useViewer();
  const base = `/api/v1/organizer/organizations/${organizationId ?? ''}`;
  const command = useCommand<OrganizerEvent>();

  const { resource: venues } = useResource<{ venues: VenueSummary[] }>(
    organizationId === undefined ? null : `${base}/venues`,
  );

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [venueId, setVenueId] = useState('');
  const [timezone, setTimezone] = useState('Africa/Lagos');
  const [publicSummary, setPublicSummary] = useState('');

  const effectiveSlug = slugTouched ? slug : suggestSlug(title);
  const slugProblem =
    effectiveSlug.length === 0 || slugSchema.safeParse(effectiveSlug).success
      ? undefined
      : 'Use lowercase letters, numbers and hyphens, 3–96 characters.';
  const timezoneProblem = timezoneSchema.safeParse(timezone).success
    ? undefined
    : 'Use a region name such as Africa/Lagos.';

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    const created = await command.run(`${base}/events`, {
      method: 'POST',
      body: {
        title,
        slug: effectiveSlug,
        timezone,
        venueId: venueId === '' ? null : venueId,
        publicSummary: publicSummary === '' ? null : publicSummary,
      },
      // A dropped response must not create a second event with a mangled link.
      idempotencyKey: `create-event:${viewer.userId}:${effectiveSlug}`,
    });
    if (created !== null) {
      void navigate(`/o/${organizationId ?? ''}/events/${created.eventId}`, { replace: true });
    }
  }

  const availableVenues = venues.status === 'loaded' ? venues.data.venues : [];

  return (
    <div className="gc-stack gc-stack--loose" style={{ maxWidth: '38rem' }}>
      <div className="gc-stack gc-stack--tight">
        <span className="gc-eyebrow">New event</span>
        <h2>What are you putting on?</h2>
        <p className="gc-muted">
          You can add sessions, gates, tickets and capacity next. Nothing is public until you
          publish.
        </p>
      </div>

      <Card>
        <form className="gc-stack" onSubmit={(event) => void submit(event)}>
          <FailureNotice error={command.error} />

          <Field
            label="Event title"
            required
            placeholder="Lagos Live 2026"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
          <Field
            label="Public link"
            required
            hint="Attendees will see gatecheck.example/e/… — it cannot change once the event is public."
            error={slugProblem}
            value={effectiveSlug}
            onChange={(event) => {
              setSlugTouched(true);
              setSlug(event.target.value);
            }}
          />

          <div className="gc-field">
            <label className="gc-field__label" htmlFor="venue">
              Venue
            </label>
            <select
              id="venue"
              className="gc-select"
              value={venueId}
              onChange={(event) => {
                setVenueId(event.target.value);
                const chosen = availableVenues.find(
                  (candidate) => candidate.venueId === event.target.value,
                );
                // The venue's own wall clock is almost always the right answer.
                if (chosen !== undefined) setTimezone(chosen.timezone);
              }}
            >
              <option value="">Choose later</option>
              {availableVenues.map((venue) => (
                <option key={venue.venueId} value={venue.venueId}>
                  {venue.name} — {venue.publicLocality}
                </option>
              ))}
            </select>
            {availableVenues.length === 0 && (
              <span className="gc-field__hint">
                No venues yet. You can <Link to={`/o/${organizationId ?? ''}/venues`}>add one</Link>{' '}
                now or after creating the event.
              </span>
            )}
          </div>

          <Field
            label="Timezone"
            required
            hint="The venue's own clock. Doors at 19:00 means 19:00 here, through any daylight-saving change."
            error={timezoneProblem}
            value={timezone}
            onChange={(event) => setTimezone(event.target.value)}
          />

          <Field
            label="Public summary"
            hint="The first line an attendee reads. Needed before you can publish."
            value={publicSummary}
            onChange={(event) => setPublicSummary(event.target.value)}
          />

          <Button
            type="submit"
            variant="primary"
            size="large"
            busy={command.busy}
            disabled={
              title.length < 2 || slugProblem !== undefined || timezoneProblem !== undefined
            }
          >
            Create event
          </Button>
        </form>
      </Card>

      <p className="gc-muted">
        <Link to={`/o/${organizationId ?? ''}/events`}>Back to events</Link>
      </p>
    </div>
  );
}
